import { addressLike } from "@gearbox-protocol/cli-utils";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useToast,
} from "@gearbox-protocol/deploy-tools-ui";
import { createAnvilClient, setLTZero } from "@gearbox-protocol/sdk/dev";
import { zodResolver } from "@hookform/resolvers/zod";
import type { FC } from "react";
import { useMemo } from "react";
import type { Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useUnpausedForkStatus } from "routes/fork/context";
import { useSDK } from "routes/fork/sdk-context";
import type { Address } from "viem";
import { http } from "viem";
import { z } from "zod";

import logger from "../../logger";
import type { CreditManagerOption } from "./CreditManagerInput";
import CreditManagerInput from "./CreditManagerInput";

const schema = z.object({
  creditManager: addressLike(),
});

type SetZeroLTSchema = z.output<typeof schema>;

interface SetZeroLTDialogProps {
  toggleOpen: (open: boolean) => void;
}

const SetZeroLTDialog: FC<SetZeroLTDialogProps> = ({ toggleOpen }) => {
  const { toast } = useToast();
  const { rpcUrl } = useUnpausedForkStatus();
  const { value: sdk, loading: sdkLoading } = useSDK();

  const options = useMemo<CreditManagerOption[]>(
    () =>
      sdk?.marketRegister.creditManagers.map(cm => ({
        address: cm.creditManager.address,
        label: cm.name,
      })) ?? [],
    [sdk],
  );

  const form = useForm<SetZeroLTSchema>({
    resolver: zodResolver(schema) as Resolver<SetZeroLTSchema>,
    defaultValues: { creditManager: "" as string as Address },
  });
  const value = form.watch("creditManager");
  const matched = useMemo(
    () =>
      options.find(o => o.address.toLowerCase() === value.toLowerCase().trim()),
    [options, value],
  );

  const submit = form.handleSubmit(async ({ creditManager }) => {
    if (!sdk) {
      return;
    }
    try {
      const cm = sdk.marketRegister.findCreditManager(creditManager);
      const anvil = createAnvilClient({
        chain: sdk.chain,
        transport: http(rpcUrl, { timeout: 240_000 }),
      });
      await setLTZero(anvil, cm.state, logger);
      // setLTZero does not wait for receipts; force-mine so that
      // LT update events are included in a block before syncState scans logs
      await anvil.mine({ blocks: 1 });
      toast({
        variant: "success",
        title: "LTs set to zero",
        description: `${cm.name} (${cm.creditManager.address})`,
      });
      const block = await sdk.client.getBlock({ blockTag: "latest" });
      await sdk.syncState({
        blockNumber: block.number,
        timestamp: block.timestamp,
        ignoreUpdateablePrices: true,
      });
      toggleOpen(false);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Failed to set zero LT",
        description: `${e}`,
      });
    }
  });

  const submitting = form.formState.isSubmitting;

  return (
    <Dialog open={true} onOpenChange={toggleOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Set Zero LT</DialogTitle>
          <DialogDescription>
            Set liquidation thresholds to zero for all collateral tokens of a
            credit manager
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={submit} className="space-y-2">
            <FormField
              control={form.control}
              name="creditManager"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Credit manager</FormLabel>
                  <FormControl>
                    <CreditManagerInput
                      value={field.value}
                      onChange={field.onChange}
                      options={options}
                      loading={sdkLoading}
                    />
                  </FormControl>
                  {!!matched && (
                    <p className="text-muted-foreground text-sm">
                      {matched.label}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        {submitting && (
          <p className="text-muted-foreground text-sm">
            Setting LTs to zero, this may take a while...
          </p>
        )}

        <DialogFooter>
          <Button
            type="submit"
            disabled={submitting || !sdk}
            loading={submitting}
            onClick={submit}
          >
            Set Zero LT
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SetZeroLTDialog;
